/**
 * BottomNavigation Component
 * Navigation bar with Home, List, Insights, Settings, Theme Toggle
 */

import React from 'react';
import { Home, ShoppingCart, Settings, ListTodo, Target, Wallet, Sun, Moon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const NAV_ITEMS = [
  { id: 'home', label: 'Home', icon: Home, path: '/' },
  { id: 'list', label: 'Lists', icon: ShoppingCart, path: '/shopping-lists' },
  { id: 'tasks', label: 'Tasks', icon: ListTodo, path: '/tasks' },
  { id: 'goals', label: 'Goals', icon: Target, path: '/savings-goals' },
  { id: 'budget', label: 'Budget', icon: Wallet, path: '/budget' },
  // Insights merged into Budget
  { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' }
];

export default function BottomNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  const isActive = (path) => {
    if (path === '/' && (location.pathname === '/' || location.pathname === '/dashboard')) {
      return true;
    }
    return location.pathname === path;
  };

  const isDark = theme === 'dark';

  return (
    <motion.nav
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed bottom-0 left-0 right-0 z-40 px-2 pb-2 safe-bottom pointer-events-none"
    >
      <div className="max-w-xl mx-auto">
        <div className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-2xl border border-white/20 dark:border-white/10 shadow-md px-2 py-1.5 pointer-events-auto">
          <div className="grid grid-cols-7 gap-1">
          {NAV_ITEMS.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);

            return (
              <motion.button
                key={item.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(item.path)}
                className="relative flex flex-col items-center justify-center py-1 px-1.5 rounded-xl transition-all duration-200 touch-manipulation min-w-0"
                aria-current={active ? 'page' : undefined}
              >
                {/* Active Indicator */}
                {active && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-brand-500/15 rounded-xl"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}

                {/* Icon */}
                <div className="relative flex-shrink-0">
                  <Icon
                    size={18}
                    className={`transition-colors duration-200 ${
                      active ? 'text-brand-600' : 'text-slate-500 dark:text-slate-300'
                    }`}
                  />
                  
                  {/* Active Dot */}
                  {active && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-[#006D77] rounded-full"
                    />
                  )}
                </div>

                {/* Label */}
                <span
                  className={`text-[10px] mt-0.5 font-semibold transition-colors duration-200 truncate max-w-full ${
                    active ? 'text-brand-600' : 'text-slate-600 dark:text-slate-300'
                  }`}
                >
                  {item.label}
                </span>
              </motion.button>
            );
          })}

          {/* Theme Toggle Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleTheme}
            className="relative flex flex-col items-center justify-center py-1 px-1.5 rounded-xl transition-all duration-200 touch-manipulation min-w-0"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {/* Icon */}
            <div className="relative flex-shrink-0">
              {isDark ? (
                <Sun size={18} className="text-amber-500 transition-colors duration-200" />
              ) : (
                <Moon size={18} className="text-slate-500 transition-colors duration-200" />
              )}
            </div>

            {/* Label */}
            <span className="text-[10px] mt-0.5 font-semibold text-slate-600 dark:text-slate-300 truncate max-w-full">
              {isDark ? 'Light' : 'Dark'}
            </span>
          </motion.button>
          </div>
        </div>
      </div>

    </motion.nav>
  );
}
